"use client";

import {
    ACTIVATE_WALLHALLA_MANIFEST_MUTATION,
    START_PROCESS_WALLHALLA_MUTATION,
} from "@/graphql/mutations/uploadMutations";
import { JOB_STATUS_QUERY } from "@/graphql/queries/uploadQueries";
import { ALL_WALLHALLA_MANIFESTS_QUERY } from "@/graphql/queries/wallhallaQueries";
import { uploadZipMultipart } from "@/lib/multipartUpload";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiUploadCloud } from "react-icons/fi";

interface WallhallaManifest {
    id: string;
    version: number;
    isActive: boolean;
    sourceWidth: number;
    sourceHeight: number;
    stripCount: number;
    createdAt: string;
    manifestUrl: string;
}

export default function UploadWallhallaPage() {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);

    const [taskId, setTaskId] = useState<string | null>(null);
    const [processingPct, setProcessingPct] = useState(0);

    const client = useApolloClient();
    const [startProcess] = useMutation(START_PROCESS_WALLHALLA_MUTATION);
    const [activateManifest, { loading: activating }] = useMutation(
        ACTIVATE_WALLHALLA_MANIFEST_MUTATION,
    );
    const [activatingId, setActivatingId] = useState<string | null>(null);

    const {
        data: manifestsData,
        loading: manifestsLoading,
        refetch: refetchManifests,
    } = useQuery(ALL_WALLHALLA_MANIFESTS_QUERY, { fetchPolicy: "no-cache" });

    const manifests: WallhallaManifest[] =
        manifestsData?.allWallhallaManifests ?? [];

    const handleActivate = async (manifestId: string) => {
        setActivatingId(manifestId);
        try {
            await activateManifest({ variables: { manifestId } });
            await refetchManifests();
        } catch (err) {
            console.error(err);
        } finally {
            setActivatingId(null);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = Array.from(e.target.files ?? []);
        if (selected.length === 0) return;

        setFiles((prev) => {
            const existing = new Set(
                prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`),
            );
            const next = [...prev];
            for (const file of selected) {
                const id = `${file.name}-${file.size}-${file.lastModified}`;
                if (!existing.has(id)) {
                    next.push(file);
                    existing.add(id);
                }
            }
            return next;
        });
    };

    const clearFileInput = () => {
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeFileAt = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const moveFile = (index: number, direction: -1 | 1) => {
        setFiles((prev) => {
            const nextIndex = index + direction;
            if (nextIndex < 0 || nextIndex >= prev.length) return prev;
            const next = [...prev];
            const temp = next[index];
            next[index] = next[nextIndex];
            next[nextIndex] = temp;
            return next;
        });
    };

    const handleClick = () => fileInputRef.current?.click();

    const handleUpload = async () => {
        if (files.length === 0) return;
        setUploading(true);
        setUploadPct(0);
        setProcessingPct(0);
        setTaskId(null);

        try {
            const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
            let uploadedBefore = 0;
            const keys: string[] = [];

            for (const file of files) {
                const { key } = await uploadZipMultipart(
                    client,
                    file,
                    (done, total) => {
                        const uploaded = uploadedBefore + Math.min(done, total);
                        setUploadPct(
                            Math.round(
                                (uploaded / Math.max(totalBytes, 1)) * 100,
                            ),
                        );
                    },
                    4,
                    "wallhalla",
                );

                keys.push(key);
                uploadedBefore += file.size;
                setUploadPct(
                    Math.round(
                        (uploadedBefore / Math.max(totalBytes, 1)) * 100,
                    ),
                );
            }

            const res = await startProcess({ variables: { keys } });
            setTaskId(res.data.startProcessWallhalla.taskId);
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false);
            clearFileInput();
        }
    };

    // Poll job status when we have a taskId
    const {
        data: statusData,
        startPolling,
        stopPolling,
    } = useQuery(JOB_STATUS_QUERY, {
        variables: { taskId },
        skip: !taskId,
        pollInterval: 1500,
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (!taskId) return;
        startPolling(1500);
        return () => stopPolling();
    }, [taskId, startPolling, stopPolling]);

    useEffect(() => {
        const s = statusData?.jobStatus;
        if (!s) return;
        if (typeof s.percent === "number") setProcessingPct(s.percent);
        if (s.state === "SUCCESS" || s.state === "FAILURE") {
            stopPolling();
            if (s.state === "SUCCESS") refetchManifests();
        }
    }, [statusData, stopPolling, refetchManifests]);

    const job = statusData?.jobStatus;
    const finished = job?.state === "SUCCESS" || job?.state === "FAILURE";

    return (
        <div className="mx-auto mt-4 w-full px-4">
            {/* Row 1: Upload Form + Docs */}
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* LEFT: Upload Form */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <h1 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Upload Wallhalla TIFs
                    </h1>

                    <div className="relative mb-4">
                        <div
                            className={clsx(
                                "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors duration-150",
                                files.length > 0
                                    ? "border-brand bg-brand/10 hover:bg-brand/20 dark:bg-brand/10 dark:hover:bg-brand/20"
                                    : "border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800",
                            )}
                            onClick={handleClick}
                        >
                            <FiUploadCloud className="h-10 w-10 text-slate-600 dark:text-slate-300" />
                            <p className="text-sm text-slate-700 dark:text-slate-400">
                                {files.length > 0
                                    ? `${files.length} file(s) selected`
                                    : "Click to select one or more .tif files"}
                            </p>
                        </div>

                        {files.length > 0 && (
                            <button
                                onClick={clearFileInput}
                                className="absolute top-2 right-2 cursor-pointer text-sm text-slate-600 hover:text-red-500 dark:text-slate-300"
                                title="Clear files"
                                type="button"
                            >
                                ×
                            </button>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".tif,.tiff"
                            multiple
                            onChange={handleFileChange}
                            className="hidden"
                        />
                    </div>

                    {files.length > 0 && (
                        <div className="mb-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                Left to right order (stitched in this order):
                            </p>
                            {files.map((f, index) => (
                                <div
                                    key={`${f.name}-${f.size}-${f.lastModified}`}
                                    className="flex items-center justify-between gap-2 rounded bg-white px-2 py-1.5 text-sm dark:bg-slate-900"
                                >
                                    <div className="min-w-0 text-slate-700 dark:text-slate-300">
                                        <span className="mr-2 font-semibold">
                                            {index + 1}.
                                        </span>
                                        <span className="truncate">
                                            {f.name}
                                        </span>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => moveFile(index, -1)}
                                            disabled={index === 0 || uploading}
                                            className="cursor-pointer rounded p-1 text-slate-600 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700"
                                            title="Move left"
                                        >
                                            <FiArrowLeft />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => moveFile(index, 1)}
                                            disabled={
                                                index === files.length - 1 ||
                                                uploading
                                            }
                                            className="cursor-pointer rounded p-1 text-slate-600 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700"
                                            title="Move right"
                                        >
                                            <FiArrowRight />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeFileAt(index)}
                                            disabled={uploading}
                                            className="cursor-pointer rounded px-2 py-1 text-xs text-red-600 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-red-400 dark:hover:bg-red-900/30"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <button
                        disabled={files.length === 0 || uploading}
                        onClick={handleUpload}
                        className={clsx(
                            "w-full rounded-md px-4 py-2 font-medium transition-colors",
                            uploading
                                ? "cursor-not-allowed bg-slate-300 text-slate-500 dark:bg-slate-800 dark:text-slate-500"
                                : "bg-brand hover:bg-brand/90 cursor-pointer text-white",
                        )}
                    >
                        {uploading ? "Uploading..." : "Upload & Process"}
                    </button>

                    {/* Upload progress */}
                    {uploading && (
                        <div className="mt-3 h-2 w-full rounded bg-slate-200 dark:bg-slate-700">
                            <div
                                className="bg-brand h-2 rounded"
                                style={{ width: `${uploadPct}%` }}
                            />
                        </div>
                    )}

                    {/* Processing progress */}
                    {taskId && (
                        <div className="mt-4">
                            <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
                                Processing Wallhalla image
                                {job ? ` - ${job.percent ?? 0}%` : ""}
                            </p>
                            <div className="h-2 w-full rounded bg-slate-200 dark:bg-slate-700">
                                <div
                                    className="bg-brand h-2 rounded"
                                    style={{ width: `${processingPct}%` }}
                                />
                            </div>
                            {job && (
                                <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                                    {job.processed ?? 0}/{job.total ?? 0} strips
                                    processed
                                </p>
                            )}
                        </div>
                    )}

                    {/* Results */}
                    {finished && job?.state === "SUCCESS" && (
                        <div className="mt-4">
                            <div className="rounded-lg bg-green-100 p-4 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                <p className="font-semibold">
                                    Wallhalla tiles generated and published
                                    successfully!
                                </p>
                            </div>
                        </div>
                    )}
                    {finished && job?.state === "FAILURE" && (
                        <div className="mt-4">
                            <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                                <p className="mb-1 font-semibold">Error</p>
                                <p className="text-sm">
                                    {job.result?.error ??
                                        "Processing failed. Check server logs."}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: Docs Panel */}
                <div className="rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                        Wallhalla Upload Guide
                    </h2>
                    <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-400">
                        1. Export Wallhalla image sections from Photoshop as
                        flat <strong>TIF files</strong>.
                        <br />
                        2. Select one or more <strong>.tif</strong> files using
                        the panel on the left.
                        <br />
                        3. Set the left-to-right order before upload. The files
                        are stitched in that order.
                        <br />
                        4. Click <strong>Upload &amp; Process</strong>. Files
                        are uploaded in chunks (this may take a while for large
                        files).
                        <br />
                        5. The server stitches, tiles, and publishes the final
                        Wallhalla image. Progress is shown above.
                    </p>
                    <p className="mt-4 text-xs text-slate-500 dark:text-slate-500">
                        The last 5 versions are kept. Older tile sets are
                        automatically cleaned up.
                    </p>
                </div>
            </div>

            {/* Row 2: Version History */}
            <div className="mt-8 rounded-2xl border border-slate-300 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-slate-100">
                    Version History
                </h2>

                {manifestsLoading ? (
                    <p className="text-sm text-slate-500">
                        Loading versions...
                    </p>
                ) : manifests.length === 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        No versions uploaded yet.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {manifests.map((m) => (
                            <div
                                key={m.id}
                                className={clsx(
                                    "flex items-center justify-between rounded-lg border p-4",
                                    m.isActive
                                        ? "border-green-400 bg-green-50 dark:border-green-600 dark:bg-green-900/20"
                                        : "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800",
                                )}
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-slate-900 dark:text-slate-100">
                                            v{m.version}
                                        </span>
                                        {m.isActive && (
                                            <span className="rounded-full bg-green-500 px-2 py-0.5 text-xs font-medium text-white">
                                                Active
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                        {new Date(m.createdAt).toLocaleString()}{" "}
                                        &middot;{" "}
                                        {m.sourceWidth.toLocaleString()}×
                                        {m.sourceHeight.toLocaleString()}px
                                        &middot; {m.stripCount} strips
                                    </p>
                                </div>

                                {!m.isActive && (
                                    <button
                                        onClick={() => handleActivate(m.id)}
                                        disabled={
                                            activating && activatingId === m.id
                                        }
                                        className={clsx(
                                            "ml-4 shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                                            activating && activatingId === m.id
                                                ? "cursor-not-allowed bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500"
                                                : "cursor-pointer bg-slate-700 text-white hover:bg-slate-900 dark:bg-slate-600 dark:hover:bg-slate-500",
                                        )}
                                    >
                                        {activating && activatingId === m.id
                                            ? "Activating..."
                                            : "Activate"}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">
                    The last 5 versions are retained. Older tile sets are
                    cleaned up automatically. Activating a version immediately
                    updates the live Wallhalla viewer.
                </p>
            </div>
        </div>
    );
}
